import {
  dateToRelativeTimeString,
  dateToString,
  dateToTimeString,
  css,
  html,
  OmniElement,
  OmniIconElement,
  OmniStyleElement,
} from 'omni-ui';
import { Om2Dropdown } from 'omni-campaign-ui';

import CentralizedDataAPI from '../services/centralized-data-api.js';
import {
  DEFAULT_CAMPAIGN_IMG_URL,
  TRANSPARENT_PIXEL_IMG_DATA,
} from '../helpers/constants.js';
import './campaign-card-list.js';
import { trackPortalEvent } from '../helpers/utils.js';

OmniIconElement.register();
OmniStyleElement.register();
Om2Dropdown.register();

/**
 * Class for Attach Objects Module Element
 * @extends OmniElement
 * @todo Maybe some of these can be extracted into other typedefs
 */
export class CampaignAttachObjects extends OmniElement {
  /**
   * Attach Objects Lit Properties
   * @property {string} env - Application environment
   * @property {Object} context - Values passed in from wrapper/router
   * @property {Object} context.clientId - The client ID to be passed to the API so objects are scoped to the client
   * @property {Object} context.uuid_module - This module's UUID
   * @property {string} token - API JWT token
   * @property {string} label - Label text for control
   * @property {string} moduleTitle - Title text for module block
   * @property {string} objectApiUUID - The ID of the object type for getting objects from the shared objects API
   * @property {SharedObject[]} objects - Stores the array of objects returned from the API
   * @property {Object[]} selectedObjects - Stores the array of objects the user has selected
   * @property {string} selectedObjects[].name - Name for display in the {@link CardListElement}
   * @property {string} selectedObjects[].url - URL (if applicable) for the {@link CardListElement} to link to
   * @property {Object[]} savedData - Data already saved in the API
   * @property {string} savedData[].uuid - The ID, used to filter selected objects on load
   * @property {Object} overview - indicates overview mode, the value is the associated data
   * @property {Object} notesOpen - (internal use) the object and temporary changes for editing notes
   * @property {Boolean} treatActionURLAsExternal - treat the passed in action URL as an external_url when its a service based object without an external_url link (see tidbits)
   * @property {Boolean} shareAudienceObjects - indicates that the objects are being saved as studio audiences
   */
  static get properties() {
    return {
      env: { type: String },
      context: { type: Object },
      token: { type: String },
      label: { type: String },
      moduleTitle: { type: String },
      objects: { type: Array },
      selectedObjects: { type: Array },
      savedData: { type: Array },
      objectApiUUID: { type: String },
      category: { type: String },
      subcategories: { type: Array },
      overview: { type: Object },
      notesOpen: { type: Object },
      treatActionURLAsExternal: { type: Boolean },
      moduleLocation: { type: String },
      shareAudienceObjects: { type: Boolean },
    };
  }

  /**
   * Attach Objects module styles
   */
  static get styles() {
    return [
      super.styles,
      css`
        .omni .module-container {
          padding: 2.14rem 2.86rem 4.29rem 2.86rem;
        }
        .control-container {
          max-width: 21.5rem;
        }
        .omni .label {
          opacity: 0.6;
          font-size: 0.785rem;
          font-weight: normal;
          line-height: normal;
          letter-spacing: 0.0275rem;
          color: rgb(var(--rgb-core-dark));
          margin: 0 0 0.56rem 0.5rem !important;
        }
        omni-card .dropdown-item omni-icon {
          vertical-align: text-bottom;
          display: inline-block;
          margin-right: 0.214rem;
        }
        .omni omni-card button.dropdown-item {
          all: unset;

          font-size: 1rem;

          color: var(--color-gray-4a);
          display: block;
          line-height: 1.5;
          padding: 0.375rem 1rem;
          position: relative;

          padding-right: 3rem;
          text-align: inherit;
          white-space: nowrap;
          width: -webkit-fill-available;
        }
        .omni omni-card .dropdown-item:hover {
          background-color: rgb(var(--rgb-core-medium-two));
        }
        .omni span.tiny-red-circle {
          display: inline-block;
          background-color: var(--color-melon);
          width: 0.857rem;
          height: 0.857rem;
          border-radius: 0.429rem;
          line-height: 0.571rem;
          color: white;
          font-size: 0.571rem;
          font-weight: bold;
          text-align: center;
          vertical-align: super;
          padding: 0.143rem 0;
        }
        .notes-modal .modal-card {
          height: 32.857rem;
        }
        .notes-modal textarea {
          height: 100%;
          resize: none !important;
        }
        .notes-modal .previous-notes h3 {
          margin: 0 !important;
          padding: 2.143rem 0 0.357rem 0;
          border-bottom: 0.071rem solid rgb(var(--rgb-border));
        }
        .notes-modal .previous-notes .note-label {
          margin: 0 !important;
          padding: 0.714rem 0 0.357rem 0.5rem !important;
          font-size: 0.857rem;
        }
        .notes-modal .previous-notes .note-text {
          margin: 0 !important;
          border-bottom: 0.071rem solid rgb(var(--rgb-border));
          padding: 0 0 0.714rem 0.5rem;
        }
        .notes-modal .previous-note omni-tooltip {
          opacity: 0;
        }
        .notes-modal .previous-note:hover omni-tooltip {
          opacity: 1;
        }
      `,
    ];
  }

  /**
   * Create OmniAttachObjectsElement and initialize {@link #context}, and {@link #objects} properties
   */
  constructor() {
    super();
    this.context = {};
    this.objects = null;
    this.selectedObjects = null;
  }

  /**
   * Initialize {@link #objectsAPI} on `this` and calls {@link #getObjects}
   * @see {@link CentralizedDataAPI}
   */
  async connectedCallback() {
    super.connectedCallback();
    this.objectsAPI = new CentralizedDataAPI(this.token, this.env);

    this.notes = structuredClone(this.savedData?.notes ?? {});
    this.moduleLocation =
      new URL(window.location.href).searchParams.get('view') === 'Assets'
        ? 'assets page'
        : 'attach objects module';

    if (this.overview) {
      this.objects = [];
      this.selectedObjects = await this._getOverviewObjects();
    } else {
      // Fire off both api requests in parallel. The results of the selected objects
      // call is redundant as it is part of all objects, but this is done so we can load
      // the selected objects faster while stil waiting on potentially thousands of
      // objects.
      const selectedObjectsPromise = this._getSelectedObjects(
        this.savedData?.objects,
        () => !this.objectApiUUID && !!this.category && !!this.subcategories,
      );
      const objectsPromise = this._getObjects({
        category: this.category,
        subcategories: this.subcategories,
        objectApiUUID: this.objectApiUUID,
        savedData: this.savedData,
      });

      // Wait for selected objects first, then all objects as it will take longer
      this.selectedObjects = await selectedObjectsPromise;
      this.objects = await objectsPromise;
    }
  }

  /**
   * Returns an array of row data to be included in csv export
   */
  get csvData() {
    return this.selectedObjects.map(x => ({
      type: 'output',
      name: x.name,
      url: x.url,
    }));
  }

  /**
   * Similar to _getObjects() but specifically for "overview" mode. We combine the uuids
   * from all modules and make a single call to shared objects for just those objects.
   * Results only include the selected (deduped) objects array as we are not loading all
   * objects.
   * @returns {array} Tuple of objects array (empty), and selected objects array
   */
  async _getOverviewObjects() {
    const configByUuid = {};

    this.overview.map(async module => {
      // Calculate and store the "includesCustomOutputs" boolean for use when
      // generating the url below (similar to _getObjects())
      const category = module.data_structure?.category;
      const subcategories = module.data_structure?.subcategories;
      const objectApiUUID = module.data_structure?.uuid_api_connection;
      const includesCustomOutputs =
        !objectApiUUID && !!category && !!subcategories;
      const uuids = module.saved_data?.objects ?? [];
      uuids.forEach(uuid => {
        configByUuid[uuid] = { includesCustomOutputs };
      });
    });

    return this._getSelectedObjects(
      Object.keys(configByUuid),
      uuid => configByUuid[uuid].includesCustomOutputs,
    );
  }

  async _getSelectedObjects(uuids, includesCustomOutputsLookup) {
    if (!uuids?.length) return [];

    // Single call with the list of all uuids
    const urlParams = new URLSearchParams([
      ['length', 'all'],
      ['client', this.context.clientId],
      ...uuids.map(uuid => ['uuid_object', uuid]),
    ]);

    // Massage the results similar to logic in _getObjects()
    const { data: results = [] } = await this.objectsAPI.request(
      'search',
      urlParams,
    );
    const objects = results.map(
      ({
        actions,
        uuid_api_connection: apiConnectionId,
        external_url: externalUrl,
        uuid_object: uuid,
        ...rest
      }) => {
        const url = this._getObjectUrl({
          url: actions?.[0]?.link,
          apiConnectionId,
          externalUrl,
          uuid,
          includesCustomOutputs: includesCustomOutputsLookup(uuid),
        });
        return {
          url,
          uuid,
          uuid_api_connection: apiConnectionId,
          ...rest,
        };
      },
    );
    return objects;
  }

  /**
   * Get available objects from the {@link ObjectsAPI}
   * and map them to {@link #objects} and filter using {@link #savedData} to
   * populate {@link #selectedObjects}. If `includesCustomOutputs` is `true`
   * it will use `'search'` as the `objectApiUUID` for the {@link ObjectsApi} `request`
   * method, and passes a modified payload that matches the requirements of the `search`
   * endpoint.
   * @param {object} options
   * @param {string} options.category
   * @param {string} options.subcategories
   * @param {string} options.objectApiUUID
   * @returns {array} List of all objects returned from shared objects API
   */
  async _getObjects({ category, subcategories, objectApiUUID }) {
    try {
      const { clientId } = this.context;
      const includesCustomOutputs =
        !objectApiUUID && !!category && !!subcategories;

      // shared parameters
      const urlParams = new URLSearchParams({
        length: 'all',
        [includesCustomOutputs ? 'client' : 'guid_client']: clientId,
      });

      if (!includesCustomOutputs) {
        urlParams.append(
          'fields',
          'uuid_object,name,image_url,uuid_api_connection,update_date',
        );
        urlParams.append('build_actions', true);
      } else {
        urlParams.append('category', category);
        // API won't serialize subcategory properly so we do it here
        subcategories.forEach(sc => urlParams.append('subcategory', sc));
      }

      const { data: results = [] } = await this.objectsAPI.request(
        includesCustomOutputs ? 'search' : objectApiUUID,
        urlParams,
      );

      const objects = results.map(
        ({
          name,
          actions,
          uuid_api_connection: apiConnectionId,
          external_url: externalUrl,
          uuid_object: uuid,
          data_points: objectDataPoints,
          ...rest
        }) => {
          const dataPoints = Array.isArray(objectDataPoints)
            ? objectDataPoints.reduce((agg, dp) => ({ ...agg, ...dp }))
            : objectDataPoints;

          const [viewAction = {}] = actions;
          const { link } = viewAction;
          const url = this._getObjectUrl({
            url: link,
            apiConnectionId,
            externalUrl,
            uuid,
            includesCustomOutputs,
          });

          return {
            url,
            uuid,
            name,
            uuid_api_connection: apiConnectionId,
            data_points: dataPoints,
            ...rest,
          };
        },
      );

      return objects;
    } catch (e) {
      throw new Error(`Failed to get objects:`, e);
    }
  }

  /**
   * Submit method passed up to {@link CampaignStepDetail} via the
   */
  async _submit() {
    let fridgeParams;

    const isAudienceObject =
      this.category === '5c2869a9-1802-4526-bc19-94561f8279b7';

    if (isAudienceObject && this.shareAudienceObjects) {
      fridgeParams = {
        '/studio/audience_segments': this.selectedObjects
          .filter(
            obj => obj.data_points.id_count && obj.data_points.pop_id_count,
          )
          .map(obj => ({
            uuid: obj.uuid,
            name: obj.name,
            populationCount: obj.data_points.pop_id_count,
            idCount: obj.data_points.id_count,
          })),
      };
    }

    await this.save(
      {
        objects: this.selectedObjects.map(obj => obj.uuid),
        notes: structuredClone(this.notes),
      },
      fridgeParams,
    );
  }

  /**
   * Update the {@link #selectedObjects} when {@link BulmaDropdownElement} changes
   * @listens BulmaDropdownElement#change
   * @param {object} event - Event object
   * @param {object} event.detail - Values passed with event
   * @param {object} event.detail.value - The new value
   */
  _handleSelectChange({ detail }) {
    const isObjectAttached =
      detail?.value.length > this.selectedObjects?.length;
    // event tracking lives here as _deselectObject also triggers this method due to @change on the dropdown
    trackPortalEvent(
      'object',
      isObjectAttached ? 'attach' : 'delete',
      this.moduleLocation,
    );

    this.selectedObjects = detail?.value;

    this._dispatchModuleChangedEvent();
  }

  /**
   * Deselect an object from {@link #selectedObjects} and any associated notes
   * @param {number} index - The index of the object in the array of {@link selectedObjects}
   */
  _deselectObject(index) {
    delete this.notes[this.selectedObjects[index].uuid];
    const previous = [...this.selectedObjects];
    previous.splice(index, 1);
    this.selectedObjects = previous;
    this._dispatchModuleChangedEvent();
  }

  /**
   * @fires OmniAttachObjectsElement~omni-module-changed
   */
  _dispatchModuleChangedEvent() {
    const { uuid_module: moduleId } = this.context;

    /**
     * @event OmniAttachObjectsElement~omni-module-changed
     * @todo Move this into typedefs or some global space for reuse?
     * @type {object}
     * @property {string} moduleId - This module's ID
     * @property {function} saveAction - A reference to {@link submit} bound to this
     * @example
     *   this.dispatchNewEvent('omni-module-changed', {
     *     detail: {
     *       moduleId,
     *       saveAction: this.submit.bind(this),
     *     },
     *   });
     */
    this.dispatchNewEvent('omni-module-changed', {
      detail: {
        moduleId,
        saveAction: this._submit.bind(this),
      },
    });
  }

  /**
   * Build the URL with the proper environment, and swapping 'prod' with an empty string.
   * If `includesCustomOutputs` is set it returns the `external_url` (`externalUrl`) property from the options argument.
   * Params are destructured.
   * @param {object} options - The shared object
   * @param {string} options.url - The final URL the user will be directed to
   * @param {string} options.apiConnectionId
   * @param {string} options.uuid
   * @param {string} options.externalUrl - The URL to be used, if provided, when `includesCustomOutputs` is `true`
   * @param {string} options.includesCustomOutputs
   */
  _getObjectUrl({
    apiConnectionId,
    uuid,
    url,
    externalUrl,
    includesCustomOutputs,
  }) {
    if (includesCustomOutputs && externalUrl?.length) return externalUrl;
    // special case for objects like tidbits that are service based but have a non-omni link (see tidbits)
    if (this.treatActionURLAsExternal) return url;

    const {
      context: { clientId },
    } = this;
    const env = this.env !== 'prod' ? this.env : '';

    return `https://${env}omni.annalect.com/open/${clientId}/${apiConnectionId}/${uuid}/${encodeURIComponent(
      url,
    )}`;
  }

  /**
   * Render method specifically for {@link BulmaDropdownElement}
   * @returns {html} Rendered HTML for the {@link BulmaDropdownElement} component
   */
  _renderDropdown() {
    return this.objects?.length
      ? html` <om2-dropdown
          ?disabled=${this.context.readonly}
          hasSearch
          nameProperty="name"
          idProperty="uuid"
          hasObjectValues
          isMulti
          @change=${this._handleSelectChange}
          .options=${this.objects}
          .value=${this.selectedObjects}
          .renderSelected=${() => html`<div>${this.placeholder}</div>`}
        ></om2-dropdown>`
      : `No ${this.label} available`;
  }

  get isLoading() {
    return !this.objects || !this.selectedObjects;
  }

  get isLoadingSelected() {
    return !this.selectedObjects;
  }

  /**
   * Get the list of saved notes for a particular object
   * @returns {array} The list of notes (or an empty array)
   */
  _getNotes(object) {
    return this.notes[object?.uuid] ?? [];
  }

  /**
   * Render the modal that displays the notes for an object and allows adding/deleting
   * @returns {html} Rendered HTML
   */
  _renderNotesModal() {
    if (!this.notesOpen) return '';

    const formatDate = dateString => {
      const d = new Date(dateString);
      return `${dateToString(d)} ${dateToTimeString(d)}`;
    };
    const closeModal = () => {
      this.notesOpen = undefined;
    };
    const save = () => {
      const noteInput = this.shadowRoot.querySelector(
        'textarea.new-note-input',
      );
      if (noteInput?.value) {
        this.notesOpen.notes.unshift({
          author: {
            fullName: AN?.sso?.user?.fullName,
            personId: AN?.sso?.user?.personId,
          },
          date: new Date().toISOString(),
          text: noteInput?.value,
        });
      }
      trackPortalEvent('notes', 'edit', this.moduleLocation);
      // Commit the temp changes in this.notesOpen to this.notes
      this.notes[this.notesOpen.object.uuid] = this.notesOpen.notes;

      this._dispatchModuleChangedEvent();
      closeModal();
    };
    const { notes } = this.notesOpen;

    return html`
      <div class="modal is-active notes-modal">
        <div
          aria-hidden="true"
          class="modal-background"
          @click=${closeModal}
        ></div>
        <div class="modal-card">
          <header class="modal-card-head">
            <p class="modal-card-title">Notes</p>
          </header>
          <section class="modal-card-body is-flex is-flex-direction-column">
            <label class="label">Notes</label>
            <textarea
              placeholder="Enter your notes"
              class="textarea new-note-input"
              ?disabled=${this.context.readonly}
            ></textarea>
            <div class="previous-notes">
              ${notes.length === 0
                ? ''
                : html`
                    <h3 class="title is-size-6 mt-5">Previous Notes</h3>
                    ${notes.map(
                      (note, index) => html`
                        <div class="previous-note">
                          ${note.author?.personId === AN?.sso?.user?.personId
                            ? html` <omni-tooltip class="is-pulled-right mt-1">
                                <button
                                  slot="invoker"
                                  class="button is-text is-size-7 ${this.context
                                    .readonly
                                    ? 'is-hidden'
                                    : ''}"
                                  @click=${() => {
                                    notes.splice(index, 1);
                                    this.requestUpdate();
                                  }}
                                >
                                  <omni-icon
                                    icon-id="omni:interactive:trash"
                                    class="is-size-3"
                                  ></omni-icon>
                                </button>
                                <div slot="content">Delete Note</div>
                              </omni-tooltip>`
                            : ''}
                          <label class="label note-label"
                            >${note.author?.fullName} |
                            ${formatDate(note.date)}</label
                          >
                          <div class="note-text">${note.text}</div>
                        </div>
                      `,
                    )}
                  `}
            </div>
          </section>
          <footer class="modal-card-foot is-justify-content-right">
            <button
              class="button has-text-primary is-text"
              @click=${closeModal}
            >
              Cancel
            </button>
            <button
              class="button is-primary"
              @click=${save}
              ?disabled=${this.context.readonly}
            >
              Update
            </button>
          </footer>
        </div>
      </div>
    `;
  }

  /**
   * Render the list of objects using {@link OmniCardElement}
   * @returns {html} Rendered HTML of all objects in a card grid
   */
  _renderCardGrid() {
    let objects = this.selectedObjects;
    if (!objects) {
      if (this.overview) {
        // Create dummy objects just to render cards and mark as loading
        let numObjects = 0;
        this.overview.forEach(module => {
          numObjects += module.saved_data?.objects?.length || 0;
        });
        objects = new Array(numObjects).fill({});
      } else {
        objects = [];
      }
    }

    if (this.overview && objects.length === 0)
      return html`<slot name="empty-overview"></slot>`;

    const getImage = obj => {
      if (this.isLoadingSelected) {
        return TRANSPARENT_PIXEL_IMG_DATA;
      }
      return (
        obj?.image_url ||
        obj?.category?.wallpaper_url ||
        DEFAULT_CAMPAIGN_IMG_URL
      );
    };

    const renderNoteLength = obj => {
      const len = this._getNotes(obj).length;
      return len === 0 ? '' : html`<span class="tiny-red-circle">${len}</span>`;
    };

    return html`
      <omni-style>
        <omni-card-grid>
          ${objects.map(
            (obj, index) => html`
              <omni-card
                href="javascript: void(0);"
                openMsg="Open"
                @open=${e => {
                  e.preventDefault();
                  window.open(
                    obj?.url,
                    obj?.dataPoints?.open_in_same_tab ? '_self' : '_blank',
                  );
                  trackPortalEvent('link', 'open', this.moduleLocation);
                }}
                .isLoadingCard=${this.isLoadingSelected}
              >
                <h1 slot="card-title">${obj?.name}</h1>
                <h2 slot="card-type">${obj?.category?.name}</h2>
                <img
                  slot="card-img"
                  alt="campaign"
                  loading="lazy"
                  src=${getImage(obj)}
                />
                ${obj?.category?.icon_url
                  ? html`
                      <omni-icon
                        slot="card-icon"
                        icon-id="${obj?.category?.icon_url}"
                      ></omni-icon>
                    `
                  : ''}
                <span slot="card-status">Last Modified</span>
                <time slot="card-time" datetime=${obj?.update_date}>
                  ${obj?.update_date
                    ? dateToRelativeTimeString(new Date(obj?.update_date))
                    : 'N/A'}
                </time>
                ${this.overview || this.context.readonly
                  ? ''
                  : html`
                      <div slot="card-menu" class="dropdown-content ">
                        <button
                          class="dropdown-item ${obj?.sub_category?.name
                            ? ''
                            : 'is-hidden'}"
                          tabindex="0"
                          @click=${e => {
                            e.preventDefault();
                            trackPortalEvent(
                              'teams',
                              'share',
                              this.moduleLocation,
                            );
                            this.dispatchNewEvent('teams-modal-open', {
                              detail: { ...obj, type: obj?.sub_category?.name },
                            });
                          }}
                        >
                          <omni-icon
                            class="is-size-1"
                            icon-id="omni:app:teams"
                          ></omni-icon>
                          Discuss
                        </button>
                        <button
                          class="dropdown-item"
                          tabindex="0"
                          @click=${e => {
                            e.preventDefault();
                            this.notesOpen = {
                              object: obj,
                              notes: structuredClone(this._getNotes(obj)),
                            };
                          }}
                          }
                        >
                          Notes ${renderNoteLength(obj)}
                        </button>
                        <button
                          class="dropdown-item"
                          tabindex="0"
                          @click=${e => {
                            e.preventDefault();
                            this._deselectObject(index);
                          }}
                        >
                          Remove
                        </button>
                      </div>
                    `}
              </omni-card>
            `,
          )}
        </omni-card-grid>
      </omni-style>
    `;
  }

  /**
   * @returns {html} Rendered HTML for the component
   */
  render() {
    /**
     * @member {html} control - Conditionally render the dropdown
     * @memberof OmniAttachObjectsElement#render
     */
    if (this.overview) return this._renderCardGrid();

    return html`
      <omni-style>
        <div class="box module-container">
          <h2 class="title mb-5 is-3">${this.moduleTitle}</h2>
          <div class="control-container mb-5">
            <form>
              <div class="field m-0">
                <label class="label">${this.label}</label>
                <div class="control">
                  ${this.isLoading
                    ? html`Loading&hellip;`
                    : this._renderDropdown()}
                </div>
              </div>
            </form>
          </div>
          ${this._renderCardGrid()} ${this._renderNotesModal()}
        </div>
      </omni-style>
    `;
  }
}

OmniElement.register('campaign-attach-objects', CampaignAttachObjects);
